import React, { useRef, useEffect, useState, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports -- CSS dosyalari barrel'dan ihraç edilemez, deep import zorunlu
import '../../modules/dxf/components/DxfViewer.css';

const PdfViewer = ({ file, url, className = '', height = 300 }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pagesImgRef = useRef([]);
  const layoutRef = useRef({ totalW: 0, totalH: 0 });

  const [viewState, setViewState] = useState(null); // null until fit is calculated
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageCount, setPageCount] = useState(0);

  const dragRef = useRef({ dragging: false, lastX: 0, lastY: 0 });
  const touchRef = useRef({ touches: [], lastDist: 0 });

  // Load PDF and render all pages to offscreen canvases
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setViewState(null);
    pagesImgRef.current = [];

    (async () => {
      try {
        await import('pdfjs-dist/build/pdf.worker.entry');
        const pdfjsLib = await import('pdfjs-dist/build/pdf');

        let src;
        if (file) {
          src = { data: new Uint8Array(await file.arrayBuffer()) };
        } else if (url) {
          src = url;
        } else {
          throw new Error('No source');
        }

        const pdf = await pdfjsLib.getDocument(src).promise;
        if (cancelled) return;
        setPageCount(pdf.numPages);

        const dpr = window.devicePixelRatio || 1;
        const RENDER_SCALE = Math.max(3, Math.ceil(dpr * 2));
        const GAP = 12;
        const pages = [];
        let currentY = 0;

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          if (cancelled) return;
          const vp = page.getViewport({ scale: RENDER_SCALE });
          const offCanvas = document.createElement('canvas');
          offCanvas.width = vp.width;
          offCanvas.height = vp.height;
          const offCtx = offCanvas.getContext('2d');
          await page.render({ canvasContext: offCtx, viewport: vp }).promise;
          if (cancelled) return;

          pages.push({
            canvas: offCanvas,
            renderScale: RENDER_SCALE,
            w: vp.width / RENDER_SCALE,
            h: vp.height / RENDER_SCALE,
            y: currentY,
          });
          currentY += vp.height / RENDER_SCALE + GAP;
        }

        if (cancelled) return;
        pagesImgRef.current = pages;

        const totalW = Math.max(...pages.map(p => p.w));
        const totalH = currentY - GAP;
        layoutRef.current = { totalW, totalH };
      } catch (err) {
        console.error('PDF load error:', err);
        if (!cancelled) setError('PDF yuklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [file, url]);

  // After loading completes and canvas is in DOM, size it and fit
  useEffect(() => {
    if (loading || !pagesImgRef.current.length) return;
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);

    const { totalW, totalH } = layoutRef.current;
    const padding = 0.92;
    const s = Math.min((rect.width * padding) / totalW, (rect.height * padding) / totalH);
    const ox = (rect.width / 2 - (totalW / 2) * s) * dpr;
    const oy = (rect.height / 2 - (totalH / 2) * s) * dpr;
    setViewState({ scale: s * dpr, offsetX: ox, offsetY: oy });
  }, [loading]);

  // Render pages to visible canvas
  useEffect(() => {
    if (!viewState) return;
    const canvas = canvasRef.current;
    if (!canvas || !pagesImgRef.current.length) return;
    const ctx = canvas.getContext('2d');
    const { scale, offsetX, offsetY } = viewState;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#2a2a30';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);

    // Enable image smoothing for downscaled high-res renders
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const totalW = layoutRef.current.totalW;

    for (const page of pagesImgRef.current) {
      const px = (totalW - page.w) / 2;

      ctx.fillStyle = 'rgba(0,0,0,.3)';
      ctx.fillRect(px + 3, page.y + 3, page.w, page.h);

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(px, page.y, page.w, page.h);

      ctx.drawImage(page.canvas, px, page.y, page.w, page.h);
    }

    ctx.restore();
  }, [viewState]);

  // Mouse pan
  const handleMouseDown = useCallback((e) => {
    dragRef.current = { dragging: true, lastX: e.clientX, lastY: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const dpr = window.devicePixelRatio || 1;
    const dx = (e.clientX - dragRef.current.lastX) * dpr;
    const dy = (e.clientY - dragRef.current.lastY) * dpr;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
    setViewState(v => v && ({ ...v, offsetX: v.offsetX + dx, offsetY: v.offsetY + dy }));
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current.dragging = false;
  }, []);

  // Wheel zoom
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const mx = (e.clientX - rect.left) * dpr;
      const my = (e.clientY - rect.top) * dpr;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      setViewState(v => v && ({
        scale: v.scale * factor,
        offsetX: mx - (mx - v.offsetX) * factor,
        offsetY: my - (my - v.offsetY) * factor,
      }));
    };
    canvas.addEventListener('wheel', handler, { passive: false });
    return () => canvas.removeEventListener('wheel', handler);
  }, [loading]);

  // Touch pan/zoom
  const handleTouchStart = useCallback((e) => {
    const touches = Array.from(e.touches);
    touchRef.current.touches = touches.map(t => ({ x: t.clientX, y: t.clientY }));
    if (touches.length === 2) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      touchRef.current.lastDist = Math.sqrt(dx * dx + dy * dy);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    const touches = Array.from(e.touches);
    const prev = touchRef.current.touches;
    const dpr = window.devicePixelRatio || 1;

    if (touches.length === 1 && prev.length >= 1) {
      const dx = (touches[0].clientX - prev[0].x) * dpr;
      const dy = (touches[0].clientY - prev[0].y) * dpr;
      setViewState(v => v && ({ ...v, offsetX: v.offsetX + dx, offsetY: v.offsetY + dy }));
    } else if (touches.length === 2 && prev.length >= 2) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const factor = dist / (touchRef.current.lastDist || dist);
      const cx = (touches[0].clientX + touches[1].clientX) / 2;
      const cy = (touches[0].clientY + touches[1].clientY) / 2;
      const canvas = canvasRef.current;
      const rect = canvas?.getBoundingClientRect();
      const mx = (cx - (rect?.left || 0)) * dpr;
      const my = (cy - (rect?.top || 0)) * dpr;
      setViewState(v => v && ({
        scale: v.scale * factor,
        offsetX: mx - (mx - v.offsetX) * factor,
        offsetY: my - (my - v.offsetY) * factor,
      }));
      touchRef.current.lastDist = dist;
    }
    touchRef.current.touches = touches.map(t => ({ x: t.clientX, y: t.clientY }));
  }, []);

  // Reset view (fit all pages)
  const handleReset = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas || !pagesImgRef.current.length) return;
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const { totalW, totalH } = layoutRef.current;
    const padding = 0.92;
    const s = Math.min((rect.width * padding) / totalW, (rect.height * padding) / totalH);
    const ox = (rect.width / 2 - (totalW / 2) * s) * dpr;
    const oy = (rect.height / 2 - (totalH / 2) * s) * dpr;
    setViewState({ scale: s * dpr, offsetX: ox, offsetY: oy });
  }, []);

  if (loading) {
    return (
      <div className={`dxf-viewer dxf-viewer-empty ${className}`} style={{ height }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#666', fontSize: '.8rem' }}>
          <div style={{ width: 24, height: 24, border: '2px solid rgba(167,82,175,.2)', borderTopColor: 'rgba(167,82,175,.8)', borderRadius: '50%', animation: 'pdfSpin .7s linear infinite' }} />
          <span>PDF yukleniyor...</span>
          <style>{`@keyframes pdfSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`dxf-viewer dxf-viewer-empty ${className}`} style={{ height }}>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`dxf-viewer ${className}`}
      style={{ height }}
    >
      <canvas
        ref={canvasRef}
        className="dxf-viewer-canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => { touchRef.current.touches = []; }}
      />
      <div className="dxf-viewer-controls">
        <button onClick={() => setViewState(v => v && ({ ...v, scale: v.scale * 1.3 }))} title="Yakinlastir">+</button>
        <button onClick={() => setViewState(v => v && ({ ...v, scale: v.scale / 1.3 }))} title="Uzaklastir">-</button>
        <button onClick={handleReset} title="Sifirla">&#x27F3;</button>
      </div>
      {pageCount > 1 && (
        <div className="dxf-viewer-hint">{pageCount} sayfa</div>
      )}
    </div>
  );
};

export default PdfViewer;
